import {
  checklistOverviewSchema,
  estateDetailSchema,
  syncResultSchema,
  syncStatusSchema,
} from '@acasa/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { apiFetch } from '@/lib/api'

/**
 * The whole matrix in one request. Filtering and sorting happen in the browser,
 * so there are no query parameters and no refetch on every keystroke.
 */
export function useChecklists() {
  return useQuery({
    queryKey: ['checklists'],
    queryFn: () => apiFetch('/checklists', checklistOverviewSchema),
  })
}

export const syncStatusKey = ['checklists', 'sync-status'] as const

/**
 * Triggers a fresh read from onOffice, then refetches the overview.
 *
 * The status is refreshed whichever way it ends. A 409 means a run — usually
 * the scheduled one — is already in flight, and the button has to learn that
 * now rather than at the next poll, or it stays clickable for up to a minute
 * and answers every click with the same refusal.
 */
export function useSyncChecklists() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => apiFetch('/checklists/sync', syncResultSchema, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['checklists'] }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: syncStatusKey }),
  })
}

/**
 * Whether a sync is running, how the last one went and when the next one is
 * due. Served from SQLite and the scheduler's own state — no onOffice call —
 * so polling it and refetching on focus cost nothing worth counting.
 *
 * `nextRunAt` comes from the server on purpose: "06:00 in Berlin" is a
 * timezone question the browser must not answer differently from the
 * scheduler.
 *
 * Keyed UNDER ['checklists'], so anything that invalidates the overview — a
 * sync, a saved setting (including the schedule, which moves `nextRunAt`) —
 * refreshes the status with it. It is a separate
 * cache entry all the same: a query's key is matched whole.
 */
export function useSyncStatus(enabled = true) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: syncStatusKey,
    enabled,
    queryFn: () => apiFetch('/checklists/sync/status', syncStatusSchema),
    // Tighter while a run is going, so the button comes back soon after it
    // ends; a full sync takes long enough that 15 s is not felt as lag.
    refetchInterval: (current) => (current.state.data?.running ? 15_000 : 60_000),
    refetchOnWindowFocus: true,
  })

  /*
   * A SCHEDULED run finishes without anybody on this tab having asked for it,
   * so nothing else would tell the matrix and the dashboard that the mirror
   * under them was rebuilt. When the last run's finish time moves, they are
   * stale. The first value seen is only remembered — a page load is not a
   * finished sync.
   */
  const finishedAt = query.data ? (query.data.lastRun?.finishedAt ?? null) : undefined
  const seen = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (finishedAt === undefined) return
    if (seen.current !== undefined && seen.current !== finishedAt) {
      void queryClient.invalidateQueries({ queryKey: ['checklists'], exact: true })
      void queryClient.invalidateQueries({ queryKey: ['estate-detail'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    }
    seen.current = finishedAt
  }, [finishedAt, queryClient])

  return query
}

/**
 * Full attributes of one estate, loaded only when a row is opened.
 * Served from the local mirror, so it is fast and costs no onOffice quota.
 */
export function useEstateDetail(estateId: number | null) {
  return useQuery({
    queryKey: ['estate-detail', estateId],
    queryFn: () => apiFetch(`/checklists/estates/${estateId}`, estateDetailSchema),
    enabled: estateId !== null,
    staleTime: 5 * 60_000,
  })
}
